import PDFViewer from '../components/PDFViewer';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
// import { Document, Page, pdfjs } from 'react-pdf';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { executeReport, downloadReport, exportToExcel } from '../services/reportService';
import LoadingIndicator from '../components/LoadingIndicator';
import { toast } from 'react-toastify';
import signalRService from '../services/signalrService';
import '../styles/ViewReport2014Page.css';
import { useSession } from '../context/SessionContext';
import { authService } from '../services/authService';

// PDF.js worker setup for react-pdf (use worker from public folder directly)
// pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Helper to convert base64 to Uint8Array for PDF.js
const base64ToUint8Array = (base64) => {
  const cleaned = base64.replace(/^data:application\/pdf;base64,/, '').replace(/\s/g, '');
  const binaryString = window.atob(cleaned);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

// Define API URL from environment variable
const API_URL = process.env.REACT_APP_API_URL;

// Check if two parameters have a cascading relationship based on their names or roles
const hasCascadingRelationship = (parentParam, childParam) => {
  // Simple check based on parameter naming
  return false;
};

// Get a selection message for parameters based on dependency state
const getParameterSelectionMessage = (paramName) => {
  // Default message when no specific rule applies
  return '';
};

// ── Session storage cache helpers ──
const getSessionCache = (key) => {
  try { const raw = sessionStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch { return null; }
};
const setSessionCache = (key, data) => {
  try { sessionStorage.setItem(key, JSON.stringify(data)); } catch { /* quota exceeded or unavailable — skip silently */ }
};

const ViewReport2014Page = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  
   const location = useLocation();
  // Use state from navigation as fallback for name/description
  const navState = location.state || {};
  // Use rptname/rptdesr from navigation state as fallback for reportName/Description
  const [report, setReport] = useState(
    navState.rptname || navState.rptdesr || navState.reportName || navState.reportDescription
      ? {
          reportName: navState.rptname || navState.reportName || '',
          reportDescription: navState.rptdesr || navState.reportDescription || ''
        }
      : null
  );
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);
  const [reportParams, setReportParams] = useState({});
  const [showReport, setShowReport] = useState(false);
  const [reportUrl, setReportUrl] = useState('');
  const [menuId, setMenuId] = useState(null);
  const [htmlContent, setHtmlContent] = useState(null); // State to store HTML content
  const [pdfContent, setPdfContent] = useState(null); // State to store PDF base64
  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [activeDatePicker, setActiveDatePicker] = useState(null); // Added missing state for date picker
  const [calendarNavigation, setCalendarNavigation] = useState({}); // State to track calendar month/year navigation
  const [showExportDropdown, setShowExportDropdown] = useState(false); // State for export dropdown visibility
  const [connectionId, setConnectionId] = useState(null); // Added connection ID state
  // Add state variables for button visibility
  const [showMethodologyButton, setShowMethodologyButton] = useState(false);
  const [showExportToExcelButton, setShowExportToExcelButton] = useState(false);
  const [showViewReportButton, setShowViewReportButton] = useState(false);
  const [showExportButton, setShowExportButton] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false); // State to track if parameter section is collapsed
  const [notification, setNotification] = useState({ message: '', status: '', visible: false }); // kept for legacy showCustomNotification calls
  const [notifications, setNotifications] = useState(new Map()); // Map<jobId, {message, status, filePath}>
  const [showTooltip, setShowTooltip] = useState(false);
  const [hoveredJobId, setHoveredJobId] = useState(null);
  // Store the last export parameter summary for tooltip per job
  const exportParamSummariesRef = useRef({}); // jobId -> paramSummary
  const lastExportParamSummaryRef = useRef(null); // kept for legacy fallback
  // Loading state for individual dropdowns while fetching dependent options
  const [dropdownLoading, setDropdownLoading] = useState({});

  // Track which parameter dropdown is currently open (null = none)
  const [openParamDropdown, setOpenParamDropdown] = useState(null);

  // Close the open parameter dropdown when clicking outside of it
  useEffect(() => {
    if (!openParamDropdown) return;

    const handleClickOutside = (e) => {
      const openContainer = document.querySelector(`.checkbox-group-container[data-param="${openParamDropdown}"]`);
      if (!openContainer) return;
      if (!openContainer.contains(e.target)) {
        setOpenParamDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openParamDropdown]);

  const apiCallMadeRef = useRef(false);
  const dateFieldRefs = useRef({});
  const iframeRef = useRef(null);
  const exportButtonRef = useRef(null); // Reference to export button for positioning dropdown
  const savedReportFilesRef = useRef({}); // jobId -> fileName
  const savedReportNameRef = useRef(null); // kept for legacy handleDownloadFromNotification
  const reportStatusToastId = useRef(null); // Create a reference for the toast ID so we can update it
  const downloadStartedRef = useRef(false); // Add a new ref to track if download has started to prevent multiple API calls
  const initialCascadeRef = useRef(false); // Guard so the initial-load cascade only fires once
  const { sessionData, adfsQsParam, serviceConfiguration, serviceParam,isAdmin}=useSession();
  // Reset state when reportId changes
  useEffect(() => {
    if (reportId) {
      setLoading(true);
      setReport(null);
      setReports(null);
      setReportParams({});
      setShowReport(false);
      setHtmlContent(null); // Reset HTML content
      setError(null);
      apiCallMadeRef.current = false;
    }
  }, [reportId]);

  // Auto-dismiss individual notifications after 8 s once they reach success or error
  useEffect(() => {
    if (notifications.size === 0) return;
    const timers = [];
    notifications.forEach((n, jobId) => {
      if (n.status === 'success' || n.status === 'error') {
        const t = setTimeout(() => {
          setNotifications(prev => { const next = new Map(prev); next.delete(jobId); return next; });
        }, 8000);
        timers.push(t);
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [notifications]);
    
  // Get menuId from URL query parameters if available
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const menuIdParam = queryParams.get('menuId');
    if (menuIdParam) {
      const parsedMenuId = parseInt(menuIdParam, 10);
      setMenuId(parsedMenuId);
    }
  }, []);

  // Trigger cascade for any parameters that already have a default value on initial load
  useEffect(() => {
    if (!report || !report.parameters || initialCascadeRef.current) return;
    if (Object.keys(reportParams).length === 0) return;

    const paramsNeedingCascade = report.parameters.filter(param => {
      const val = reportParams[param.paramName];
      if (!val || val === '') return false;
      return report.parameters.some(other => {
        if (!other.dependsOn) return false;
        if (typeof other.dependsOn === 'string') return other.dependsOn === param.paramName;
        if (Array.isArray(other.dependsOn)) return other.dependsOn.includes(param.paramName);
        return false;
      });
    });

    if (paramsNeedingCascade.length > 0) {
      initialCascadeRef.current = true;
      console.log('[InitialCascade] Triggering cascade for params with defaults:', paramsNeedingCascade.map(p => p.paramName));
      paramsNeedingCascade.forEach(param => {
        handleParameterChange(param.paramName, reportParams[param.paramName]);
      });
    } else {
      initialCascadeRef.current = true;
      console.log('[InitialCascade] No params triggered. Param summary:', report.parameters.map(p => ({ name: p.paramName, type: p.paramType, dependsOn: p.dependsOn, val: reportParams[p.paramName] })));
    }
  }, [report]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch report details and parameters when we have a valid menuId
  useEffect(() => {
    if (reportId && menuId !== null && !apiCallMadeRef.current) {
      const fetchReportDetails = async () => {
        try {
          setLoading(true);
          setError(null);
          apiCallMadeRef.current = true;
          
          // Make API call to fetch report details
          
          // Ensure these parameters are fetched from the main API response with proper fallbacks
          // adfsQsParam, serviceConfiguration, and serviceParam should come from the sessionData
          // which is populated from the /main API response
          //const token = authService.getAuthTokenFromCookie();
          // ── Report details cache: skip API when same report was loaded this session ──
          const reportCacheKey = `cr_report_${reportId}_${menuId}_${sessionData.userId}`;
          let data = getSessionCache(reportCacheKey);
          if (!data) {
            const response = await axios.post(`${API_URL}/ViewReport/GetReportDetails`, {
              reportId: parseInt(reportId, 10),
              menuId: menuId,
              userID: parseInt(sessionData.userId, 10),
              adfsQsParam: adfsQsParam || {},
              adminUser: isAdmin === true || isAdmin === 'true',
              serviceConfiguration: serviceConfiguration || [],
              serviceParam: serviceParam || []
            });//, token ? { headers: { 'Authorization': `Bearer ${token}` } } : {});
            data = response.data;
            setSessionCache(reportCacheKey, data);
          }

          // Set button visibility based on the data
          if (data) {
            // Show/hide action buttons — accept both PascalCase and camelCase from server
            setShowMethodologyButton(data.BtnMethodology === true || data.btnMethodology === true);
            setShowExportToExcelButton(data.BtnExportToexcel === true || data.btnExportToexcel === true);
            setShowViewReportButton(data.BtnViewRpt === true || data.btnViewRpt === true);
            setShowExportButton(data.BtnExport === true || data.btnExport === true);
          }

          // Extract reportParameters from the response
          let parameterArray = [];
          
          // Look for reportParameters in different possible locations in the API response
          if (data && data.ReportParameters && Array.isArray(data.ReportParameters)) {
            parameterArray = data.ReportParameters;
          } else if (data && data.reportParameters && Array.isArray(data.reportParameters)) {
            parameterArray = data.reportParameters;
          } else if (data && data.parameters && Array.isArray(data.parameters)) {
            parameterArray = data.parameters;
          } else if (data && data.ReportDetails && data.ReportDetails.Parameters && Array.isArray(data.ReportDetails.Parameters)) {
            parameterArray = data.ReportDetails.Parameters;
          } else if (data && data.data && data.data.reportParameters && Array.isArray(data.data.reportParameters)) {
            parameterArray = data.data.reportParameters;
          } else {
            // Don't set sample data - if there are no parameters, we should respect that
            parameterArray = [];
          }
          
          // Normalize parameter properties for consistent access
          const normalizedParams = parameterArray.map(param => ({
            ...param,
            paramId: param.paramId || param.id || Math.random().toString(36).substr(2, 9),
            paramName: param.paramName || param.name || '',
            label: param.label || param.paramLabel || param.displayName || param.paramName || param.name || '',
            paramType: param.paramType || param.parameterType || param.type || 'Text',
            required: param.required || param.isRequired || false,
            values: param.validValues || param.values || param.options || [],
            dependsOn: param.dependsOn || []
          }));
          
          // Create processed report object with parameters
          const processedReport = {
            ...data,
            parameters: normalizedParams,
            reportName: data.ReportDetails?.RptName || data.reportDetails?.rptName || data.reportName || data.rptName || data.name || `Report ${reportId}`,
            reportDescription: data.ReportDetails?.RptDesr || data.reportDetails?.rptDesc || data.reportDescription || data.rptDesc || data.description || ""
          };
          
          // Log the API response structure for debugging
          console.log('API Response Structure:', {
            directProps: Object.keys(data),
            hasReports: !!(data.reports && Array.isArray(data.reports) && data.reports.length > 0),
            reportProps: (data.reports && Array.isArray(data.reports) && data.reports.length > 0) ? 
                         Object.keys(data.reports[0]) : [],
            rawData: data // Log the raw data to see exactly what we're getting
          });
          
          setReport(processedReport);

          // Initialize parameter values from the API response
          const initialParams = {};
          normalizedParams.forEach(param => {
            const paramName = param.paramName;
            // Handle different default value formats
            let defaultValue = '';
            
            if (param.defaultValue !== undefined && param.defaultValue !== null) {
              defaultValue = param.defaultValue;
            } else if (param.value !== undefined && param.value !== null) {
              defaultValue = param.value;
            } else if (param.paramType?.toLowerCase() === 'calendar' || 
                       param.paramType?.toLowerCase() === 'datetime' || 
                       param.paramType?.toLowerCase() === 'date') {
              // Use current date as default for date fields if no default is provided
              const today = new Date();
              defaultValue = today.toISOString().split('T')[0];
            }
            
            initialParams[paramName] = defaultValue;
          });
          
          setReportParams(initialParams);
          setLoading(false);
        } catch (err) {
          // On error, fallback to navigation state for name/description
          setReport({
            reportName: navState.rptname || navState.reportName || '',
            reportDescription: navState.rptdesr || navState.reportDescription || ''
          });
          let errorMsg;
          if (err.response?.data?.error) {
            errorMsg = err.response.data.error;
          } else if (err.response && err.response.status === 500) {
            errorMsg = 'An error occurred while generating your report. Please check your parameters and try again';
          } else {
            errorMsg = `Failed to load report details: ${err.message}`;
          }
          setError(errorMsg);
          setLoading(false);
          apiCallMadeRef.current = false; // Allow retry
        }
      };
      
      fetchReportDetails();
    }
  }, [reportId, menuId]);

  // Show date picker when calendar icon is clicked
  const showDatePicker = (paramName) => {
    // If there's already an active date picker for this param, close it
    if (activeDatePicker === paramName) {
      setActiveDatePicker(null);
      return;
    }
    
    // Set this parameter as the active date picker
    setActiveDatePicker(paramName);
  };

  // Fix the handleDateSelection function to prevent the one-day-off issue
  const handleDateSelection = (paramName, dateValue) => {
    // Create a new date object to avoid timezone issues
    const correctedDate = new Date(dateValue);
    
    // Adjust for timezone offset to prevent the one-day-off issue
    // This ensures the local date is preserved regardless of timezone
    const timezoneOffset = correctedDate.getTimezoneOffset();
    if (timezoneOffset > 0) {
      correctedDate.setMinutes(correctedDate.getMinutes() + timezoneOffset);
    }
    
    // Convert the date to ISO format for internal state, but only take the date part
    const isoDate = correctedDate.toISOString().split('T')[0];
    
    // Update the parameter value
    handleParameterChange(paramName, isoDate);
    
    // Close the date picker
    setActiveDatePicker(null);
  };

  // Update formatDateDisplay function to handle timezone issues
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Create date object from the string
      const dateObj = new Date(dateString);
      if (isNaN(dateObj)) return dateString;
      
      // Create a new date with timezone adjustment to ensure correct local date
      const correctedDate = new Date(dateObj.getTime());
      const timezoneOffset = correctedDate.getTimezoneOffset();
      if (timezoneOffset > 0) {
        correctedDate.setMinutes(correctedDate.getMinutes() + timezoneOffset);
      }
      
      // Format with padded month and day
      const month = (correctedDate.getMonth() + 1).toString().padStart(2, '0');
      const day = correctedDate.getDate().toString().padStart(2, '0');
      const year = correctedDate.getFullYear();
      
      return `${month}/${day}/${year}`;
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateString;
    }
  };

  // Handle clicking outside of date picker to close it
  useEffect(() => {
    if (activeDatePicker) {
      const handleClickOutside = (event) => {
        const datePickerElements = document.querySelectorAll('.date-picker, .date-input-wrapper');
        let clickedInside = false;
        
        datePickerElements.forEach(element => {
          if (element.contains(event.target)) {
            clickedInside = true;
          }
        });
        
        if (!clickedInside) {
          setActiveDatePicker(null);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeDatePicker]);

  // Function to render the custom date picker
  const renderDatePicker = (paramName) => {
    if (activeDatePicker !== paramName) return null;
    
    // Use navigation state if available, otherwise use current date or today
    const baseDate = calendarNavigation[paramName] || 
                     (reportParams[paramName] ? new Date(reportParams[paramName]) : new Date());
    const currentMonth = baseDate.getMonth();
    const currentYear = baseDate.getFullYear();
    
    // Generate calendar data
    const firstDay = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    // Create array of days for the month
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null); // Empty cells before the first day
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    // Generate weeks (rows of 7 days)
    const weeks = [];
    let week = [];
    days.forEach((day, index) => {
      week.push(day);
      if (index % 7 === 6 || index === days.length - 1) {
        // Fill the rest of the last week with nulls
        while (week.length < 7) week.push(null);
        weeks.push([...week]);
        week = [];
      }
    });
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];

    // Generate years array (current year - 41 to current year = 42 years total)
    const currentYearActual = new Date().getFullYear();
    const years = [];
    for (let i = currentYearActual - 41; i <= currentYearActual; i++) {
      years.push(i);
    }

    return (
      <div className="date-picker">
        <div className="date-picker-header">
          <button 
            className="nav-arrow"
            onClick={() => {
              const newNavDate = new Date(currentYear, currentMonth - 1, 1);
              setCalendarNavigation(prev => ({
                ...prev,
                [paramName]: newNavDate
              }));
            }}
          >
            ◀
          </button>
          <select 
            value={monthNames[currentMonth]}
            onChange={(e) => {
              const monthIndex = monthNames.indexOf(e.target.value);
              const newNavDate = new Date(currentYear, monthIndex, 1);
              setCalendarNavigation(prev => ({
                ...prev,
                [paramName]: newNavDate
              }));
            }}
            className="month-dropdown"
          >
            {monthNames.map((month, index) => (
              <option key={index} value={month}>{month}</option>
            ))}
          </select>
          <select 
            value={currentYear}
            onChange={(e) => {
              const newNavDate = new Date(parseInt(e.target.value), currentMonth, 1);
              setCalendarNavigation(prev => ({
                ...prev,
                [paramName]: newNavDate
              }));
            }}
            className="year-dropdown"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button 
            className="nav-arrow"
            onClick={() => {
              const newNavDate = new Date(currentYear, currentMonth + 1, 1);
              setCalendarNavigation(prev => ({
                ...prev,
                [paramName]: newNavDate
              }));
            }}
          >
            ▶
          </button>
        </div>
        <table className="date-picker-calendar">
          <thead>
            <tr>
              <th>Su</th>
              <th>Mo</th>
              <th>Tu</th>
              <th>We</th>
              <th>Th</th>
              <th>Fr</th>
              <th>Sa</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, weekIndex) => (
              <tr key={weekIndex}>
                {week.map((day, dayIndex) => {
                  const date = day ? new Date(currentYear, currentMonth, day) : null;
                  const isCurrentDay = day && date.toDateString() === (new Date()).toDateString();
                  const isSelectedDay = day && reportParams[paramName] && 
                    date.toDateString() === (new Date(reportParams[paramName])).toDateString();
                  
                  return (
                    <td 
                      key={dayIndex}
                      className={`
                        ${!day ? 'empty-day' : ''}
                        ${isCurrentDay ? 'current-day' : ''}
                        ${isSelectedDay ? 'selected-day' : ''}
                      `}
                      onClick={() => {
                        if (day) {
                          const selectedDate = new Date(currentYear, currentMonth, day);
                          handleDateSelection(paramName, selectedDate);
                        }
                      }}
                    >
                      {day}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="date-picker-footer">
          <button onClick={() => {
            const today = new Date();
            handleDateSelection(paramName, today);
          }}>Today</button>
          <button onClick={() => setActiveDatePicker(null)}>Done</button>
        </div>
      </div>
    );
  };

  // Handle parameter value changes
  const handleParameterChange = async (paramName, value, isMultiSelect = false) => {
    // Special handling for multi-select values (coming as array)
    let processedValue = value;
    if (isMultiSelect && Array.isArray(value)) {
      // Join multiple values with comma for API compatibility
      processedValue = value.join(',');
    }
    
    // Update the changed parameter value
    const updatedParams = {
      ...reportParams,
      [paramName]: processedValue
    };

    // Update UI immediately so selections show as checked without waiting
    // for dependent-parameter API responses.
    setReportParams(updatedParams);

    // Find any dependent parameters that need to be updated based on this change
    const paramsThatDependOnThis = report.parameters.filter(param => {
      // Check all possible dependsOn formats
      if (!param.dependsOn) {
        return false;
      }
      
      // If dependsOn is a string, check if it matches this parameter
      if (typeof param.dependsOn === 'string') {
        return param.dependsOn === paramName;
      }
      
      // If dependsOn is an array, check if it includes this parameter
      if (Array.isArray(param.dependsOn)) {
        return param.dependsOn.includes(paramName);
      }
      
      return false;
    });
    
    if (paramsThatDependOnThis.length > 0) {
      // Clear dependent parameters if parent value is cleared
      if (!processedValue || processedValue === '') {
        for (const dependentParam of paramsThatDependOnThis) {
          updatedParams[dependentParam.paramName] = '';
          
          // Also clear any parameters that depend on this dependent parameter (chain effect)
          const secondLevelDependents = report.parameters.filter((p) => 
            p.dependsOn && (
              (Array.isArray(p.dependsOn) && p.dependsOn.includes(dependentParam.paramName)) ||
              (typeof p.dependsOn === 'string' && p.dependsOn === dependentParam.paramName))
          );
          
          for (const secondLevelParam of secondLevelDependents) {
            updatedParams[secondLevelParam.paramName] = '';
          }
        }
        
        // Update the parameters state with all changes
        setReportParams(updatedParams);
        return;
      }
      
      // For each dependent parameter, fetch new valid values based on the parent parameter's new value
      for (const dependentParam of paramsThatDependOnThis) {
        const dependentParamName = dependentParam.paramName;
        
        // Clear existing value for dependent parameter
        updatedParams[dependentParamName] = '';
        
        try {
          // Skip API call for hidden parameters — they have no visible dropdown to populate
          const dependentParamType = (dependentParam.paramType || dependentParam.parameterType || dependentParam.type || '').toLowerCase();
          if (dependentParamType === 'hidden') {
            continue;
          }

          // Check if we need to fetch options for this dependent parameter
          const shouldFetchOptions = Array.isArray(dependentParam.dependsOn) 
            ? dependentParam.dependsOn.every(dep => updatedParams[dep] && updatedParams[dep] !== '')
            : true;
          
          if (shouldFetchOptions) {
            // indicate this dependent param is loading so we can show overlay
            setDropdownLoading(prev => ({ ...prev, [dependentParamName]: true }));
            // Prepare the request payload
            const payload = {
              reportId: parseInt(reportId, 10),
              menuId: menuId,
              userID: parseInt(sessionData.userId, 10),
              category: paramName,
              children: dependentParamName,
              adminUser: isAdmin === true || isAdmin === 'true'
            };
            
            // Add all known parameter values as knownCategoryValues
            // Pass ALL selected parameters to the backend, not just dependencies
            const knownValues = [];

            // Iterate through all parameters and include any that have values
            Object.keys(updatedParams).forEach(key => {
              const value = updatedParams[key];
              // Include parameter if it has a non-empty value
              if (value !== null && value !== undefined && value !== '') {
                knownValues.push(`${key}:${value}`);
              }
            });

            payload.knownCategoryValues = knownValues.join(';');

            // ── Cascade dropdown cache: reuse options for identical parent-value combinations ──
            const sortedKnownValues = [...knownValues].sort().join(';');
            const ddCacheKey = `cr_dd_${reportId}_${menuId}_${sessionData.userId}_${dependentParamName}_${sortedKnownValues}`;
            let responseData = getSessionCache(ddCacheKey);
            if (!responseData) {
              console.log('[Cascade] Calling GetDropDownContents. dependentParamName:', dependentParamName, 'payload:', JSON.stringify(payload));
              const response = await axios.post(
                `${API_URL}/ViewReport/GetDropDownContents`,
                payload,
                {
                  headers: {
                    'Content-Type': 'application/json'
                  }
                }
              );
              responseData = response.data;
              setSessionCache(ddCacheKey, responseData);
            } else {
              console.log('[Cascade] Cache hit for', dependentParamName);
            }

            // Process the response
            console.log('[Cascade] GetDropDownContents response for', dependentParamName, ':', JSON.stringify(responseData));
            console.log('[Cascade] Response keys:', responseData && typeof responseData === 'object' && !Array.isArray(responseData) ? Object.keys(responseData) : 'array or null');

            if (responseData && !Array.isArray(responseData)) {
              // The API returns ALL downstream dropdowns in one response.
              // Iterate every key and update whichever parameter matches.
              Object.keys(responseData).forEach(responseKey => {
                const items = responseData[responseKey];
                if (!Array.isArray(items)) return;

                // Match by exact name or case-insensitively
                const targetParam = report.parameters.find(
                  p => p.paramName === responseKey ||
                       p.paramName.toLowerCase() === responseKey.toLowerCase()
                );
                if (!targetParam) return;

                const normalizedItems = items.map(item =>
                  typeof item === 'object' ? item : { label: item, value: item }
                );

                console.log('[Cascade] Updating param', targetParam.paramName, 'with', normalizedItems.length, 'options');
                targetParam.ValidValues = normalizedItems;  // clear stale PascalCase copy
                targetParam.validValues = normalizedItems;
                targetParam.values = normalizedItems;

                // Auto-select if only one option
                if (normalizedItems.length === 1) {
                  const only = normalizedItems[0];
                  // Safely extract the value regardless of camelCase (value) or PascalCase (Value)
                  const extractedValue = typeof only === 'string' ? only
                    : (only.value != null ? String(only.value)
                      : only.Value != null ? String(only.Value) : '');
                  if (extractedValue !== '') updatedParams[targetParam.paramName] = extractedValue;
                }
              });
            } else if (responseData && Array.isArray(responseData)) {
              // Handle case where response is a direct array
              const updatedParam = report.parameters.find(p => p.paramName === dependentParamName);
              if (updatedParam) {
                updatedParam.validValues = responseData.map(item => {
                  if (typeof item === 'object') {
                    return item;
                  } else {
                    return { label: item, value: item };
                  }
                });
                updatedParam.values = updatedParam.validValues;

                // If there's only one option and it's required, auto-select it
                if (updatedParam.validValues.length === 1 && updatedParam.required) {
                  const onlyOption = updatedParam.validValues[0];
                  updatedParams[dependentParamName] = onlyOption.value || onlyOption;
                }
              }
            }

            // Force React to re-render with new dropdown options
            setReport(prev => ({ ...prev, parameters: [...prev.parameters] }));

            // Now handle any second-level dependencies
            const dependentsOfDependent = report.parameters.filter(p =>
              p.dependsOn && (
                (Array.isArray(p.dependsOn) && p.dependsOn.includes(dependentParamName)) ||
                (typeof p.dependsOn === 'string' && p.dependsOn === dependentParamName)
              )
            );

            if (dependentsOfDependent.length > 0) {
              for (const secondLevelParam of dependentsOfDependent) {
                // Clear second level dependent values
                updatedParams[secondLevelParam.paramName] = '';

                // Clear the options for second-level dependents
                const secondLevelParamObj = report.parameters.find(p => p.paramName === secondLevelParam.paramName);
                if (secondLevelParamObj) {
                  secondLevelParamObj.validValues = [];
                }
              }
            }
            // clear loading flag for this dependent param
            setDropdownLoading(prev => ({ ...prev, [dependentParamName]: false }));
          }
        } catch (error) {
          console.error(`Failed to fetch options for ${dependentParamName}:`, error);
          setDropdownLoading(prev => ({ ...prev, [dependentParamName]: false }));
        }
      }
    }
    
    // Update the parameters state with all changes
    setReportParams(updatedParams);
  };

  // Function to handle viewing the report
  const handleViewReport = async () => {
    setError(null);
    
    // Validate required parameters
    if (!validateParameters()) {
      return;
    }
    
    setExecuting(true);
    setShowReport(true); 
    try {
      // Create a copy of parameters to ensure we don't modify the state directly
      const viewParams = { ...reportParams };
      
      // Structure the request body exactly like exportReport for consistency
      const requestBody = {
        reportId: reportId.toString(),
        menuId: menuId,
        userID: parseInt(sessionData.userId, 10),
        adminUser: sessionData.isAdmin === true || sessionData.isAdmin === 'true',
        parameters: viewParams,
        format: 'MHTML',
      };
      
      const response = await axios.post(
        `${API_URL}/ViewReport/ViewReport`, 
        requestBody
      );

      // Check for PDF contentType and handle PDF rendering
      let content = null;
      let url = null;
      let contentType = response.data?.ContentType || response.data?.contentType || response.headers?.['content-type'];
      let exportContent = response.data?.ExportContent || response.data?.exportContent || response.data?.content || response.data?.Content;

      // If contentType is application/pdf and exportContent is present
      if (contentType && contentType.includes('application/pdf') && exportContent) {
        setPdfContent(exportContent);
        setShowReport(true);
        setHtmlContent(null);
        setReportUrl('');
        setExecuting(false);
        return;
      }

      // Existing logic for HTML or URL
      if (response.data && typeof response.data === 'object') {
        // Check for URL first
        if (response.data.url) {
          url = response.data.url;
        } else if (response.data.reportUrl) {
          url = response.data.reportUrl;
        } else if (response.data.Url) {
          url = response.data.Url;
        } else if (response.data.ReportUrl) {
          url = response.data.ReportUrl;
        }
        
        // If URL not found, check for HTML content
        if (!url) {
          if (response.data.content) {
            content = response.data.content;
          } else if (response.data.Content) {
            content = response.data.Content;
          } else if (response.data.html) {
            content = response.data.html;
          } else if (response.data.Html) {
            content = response.data.Html;
          } else if (response.data.reportContent) {
            content = response.data.reportContent;
          } else if (response.data.ReportContent) {
            content = response.data.ReportContent;
          }
        }
      } else if (typeof response.data === 'string') {
        // Check if the string is a URL
        if (response.data.startsWith('http') || response.data.startsWith('/')) {
          url = response.data;
        } else {
          content = response.data;
        }
      }
      
      // If we have a URL, open it in a new tab
      if (url) {
        // Make sure URL is absolute
        if (url.startsWith('/')) {
          url = `${API_URL}${url}`;
        }
        
        // Store the URL in state
        setReportUrl(url);
        
        // Open the URL in a new tab
        window.open(url, '_blank');
        
        // Reset executing state
        setExecuting(false);
        return;
      }
      
      // If we have HTML content, display it inline
      if (content) {
        // Clean and prepare the HTML content
        const cleanedContent = prepareHtmlContent(content);
        setHtmlContent(cleanedContent);
        setShowReport(true);
        setReportUrl('');
      } else {
        // No content or URL found
        throw new Error('No HTML content or report URL found in the response');
      }
// Helper to convert base64 to Uint8Array for PDF.js
const base64ToUint8Array = (base64) => {
  const cleaned = base64.replace(/^data:application\/pdf;base64,/, '').replace(/\s/g, '');
  const binaryString = window.atob(cleaned);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};
    } catch (err) {
      setError(`Failed to view report: ${err.message}`);
    } finally {
      setExecuting(false);
    }
  };
  
  // Helper function to prepare HTML content for display
  const prepareHtmlContent = (htmlString) => {
    if (!htmlString) return '';
    
    try {
      // First, fix any broken font references
      let processedHtml = htmlString.replace(
        /\/ReportServer\/Reserved\.ReportViewerWebControl\.axd\?OpType=Resource[^"']*/g, 
        '#'
      );
      
      // Also fix any image references to ensure they use absolute URLs if they're not already
      if (processedHtml.includes('src="/') && !processedHtml.includes('src="http')) {
        const baseUrl = window.location.origin;
        processedHtml = processedHtml.replace(/src="\/([^"]*)/g, `src="${baseUrl}/$1`);
      }
      
      // Add polyfills for common ASP.NET/SSRS functions
      const polyfillScript = `
        <script>
        // Polyfill for ASP.NET AJAX $get function
        if (typeof $get === 'undefined') {
          window.$get = function(id) {
            return document.getElementById(id);
          };
        }
        
        // Polyfill for __doPostBack
        if (typeof __doPostBack === 'undefined') {
          window.__doPostBack = function(eventTarget, eventArgument) {
            return false;
          };
        }
        
        // Polyfill for Microsoft AJAX
        if (typeof Sys === 'undefined') {
          window.Sys = {
            WebForms: {
              PageRequestManager: {
                getInstance: function() {
                  return {
                    _postBackSettings: {},
                    _form: {},
                    add_initializeRequest: function(){},
                    add_beginRequest: function(){},
                    add_pageLoaded: function(){},
                    add_endRequest: function(){},
                    _updateControls: function(){}
                  };
                }
              }
            },
            Application: {
              add_init: function(callback) {
                if (document.readyState === 'complete') {
                  callback();
                } else {
                  window.addEventListener('load', callback);
                }
              }
            }
          };
        }
        </script>
      `;
      
      // Try to find any document section if this is a full HTML document
      if (processedHtml.includes('<body') && processedHtml.includes('</body>')) {
        // Insert the polyfill script at the beginning of the body
        processedHtml = processedHtml.replace(/<body([^>]*)>/, `<body$1>${polyfillScript}`);
        
        const bodyMatch = /<body[^>]*>([\s\S]*)<\/body>/i.exec(processedHtml);
        if (bodyMatch && bodyMatch[1]) {
          return polyfillScript + bodyMatch[1]; // Add polyfill to extracted body content
        }
      }
      
      // Check for SSRS specific elements and extract report content
      if (processedHtml.includes('oReportDiv')) {
        const divMatch = /<div[^>]*id=["']oReportDiv["'][^>]*>([\s\S]*?)<\/div>/i.exec(processedHtml);
        if (divMatch && divMatch[0]) {
          return polyfillScript + divMatch[0]; // Add polyfill to extracted div content
        }
      }
      
      // If we can't extract specific parts, return the full processed content with polyfills
      return polyfillScript + processedHtml;
    } catch (e) {
      return htmlString;
    }
  };
  
  // Utility function to map exportOptiontype to exportType
const mapExportOptionToType = (exportOptiontype) => {
  let exportType;
  switch (exportOptiontype.toLowerCase()) {
    case "xlsx":
    case "excel":
      exportType = "xlsx";
      break;
    case "pdf":
      exportType = "pdf";
      break;
    case "docx":
    case "word":
      exportType = "docx";
      break;
    case "csv":
      exportType = "csv";
      break;
    case "xml":
      exportType = "xml";
      break;
    default:
      throw new Error(`Unsupported export option type: ${exportOptiontype}`);
  }
  return exportType;
};

  // Function to handle exporting the report
  const handleExportReport = async (exportOptiontype) => {
    // Prevent multiple simultaneous export calls
    if (executing) {
      return;
    }
    
    if (!validateParameters()) {
      return;
    }

    // Close the export dropdown immediately when export starts
    setShowExportDropdown(false);

    setError(null);
    setExecuting(true);
    setLoading(true);

    // Ensure SignalR connection is established before export
    await initializeSignalRConnection();
    
    if (!connectionId) {
      setError('SignalR connection not established');
      setExecuting(false);
      setLoading(false);
      return;
    }

    const exportType = mapExportOptionToType(exportOptiontype);
    // Generate unique job ID so each concurrent export has its own notification
    const jobId = `${exportType}-${Date.now()}`;
    
    // Remove savedReportName from parameters to avoid 500 errors on subsequent exports
    const { savedReportName, ...paramsWithoutSavedName } = reportParams;
    
    const requestBody = {
      reportId: reportId.toString(),
      menuId: menuId,
      format: exportType,
      userID: parseInt(sessionData.userId, 10),
      adminUser: sessionData.isAdmin === true || sessionData.isAdmin === 'true',
      parameters: paramsWithoutSavedName,
      connectionId: signalRService.getConnectionId() || connectionId,
      jobId: jobId
    };
    
    // Build and store the export parameters summary for this job's tooltip
    let paramSummary;
    if (report && Array.isArray(report.parameters)) {
      paramSummary = report.parameters
        .filter(param => paramsWithoutSavedName.hasOwnProperty(param.paramName))
        .map(param => {
          const value = paramsWithoutSavedName[param.paramName];
          const paramName = param.paramName;
          let displayValue = value;
          if (param.validValues && Array.isArray(param.validValues)) {
            if (typeof value === 'string' && value.includes(',')) {
              displayValue = value.split(',').map(v => {
                const trimmedValue = v.trim();
                const option = param.validValues.find(opt => 
                  String(opt.value || opt.text || opt) === trimmedValue
                );
                return option ? (option.text || option.label || option.value || option) : trimmedValue;
              }).join(',');
            } else {
              const option = param.validValues.find(opt => 
                String(opt.value || opt.text || opt) === String(value)
              );
              displayValue = option ? (option.text || option.label || option.value || option) : value;
            }
          } else if (Array.isArray(value)) {
            displayValue = value.join(',');
          }
          return `${paramName}:${displayValue}`;
        })
        .join('\n');
    } else {
      paramSummary = Object.entries(paramsWithoutSavedName)
        .map(([k, v]) => `${k}:${Array.isArray(v) ? v.join(',') : v}`)
        .join('\n');
    }
    exportParamSummariesRef.current[jobId] = paramSummary;
    lastExportParamSummaryRef.current = paramSummary; // legacy fallback
      
      // HTTP fallback: ensure notification is success even if SignalR is unreliable
      axios.post(
        `${API_URL}/ViewReport/ExportReport`,
        requestBody
      ).then(response => {
        const savedName = response?.data?.savedReportName;
        const serverFilePath = response?.data?.Filepath || response?.data?.filepath;
        if (savedName) {
          savedReportFilesRef.current[jobId] = savedName;
          savedReportNameRef.current = savedName;
          setNotifications(prev => {
            const next = new Map(prev);
            const existing = prev.get(jobId);
            if (!existing || existing.status !== 'success') {
              next.set(jobId, {
                message: `${savedName} exported successfully`,
                status: 'success',
                filePath: serverFilePath
              });
            }
            return next;
          });
        }
      }).catch(err => {
        console.error('Failed to initiate export:', err);
        setError(`Failed to initiate export: ${err.message}`);
        setExecuting(false);
        setLoading(false);
      });

      console.log('Export initiated, SignalR will handle everything...');
  };

  // Function to specifically handle "Export to Excel" button click
  const handleExportToExcel = async () => {
    // Prevent multiple simultaneous export calls
    if (executing) {
      return;
    }
    
    if (!validateParameters()) {
      return;
    }
    
    // Clear any previous errors
    setError(null);
    
    // Set executing state to true to show loading indicators
    setExecuting(true);
    setLoading(true);

    // Ensure SignalR connection is established before export
    await initializeSignalRConnection();
    
    if (!connectionId) {
      setError('SignalR connection not established');
      setExecuting(false);
      setLoading(false);
      return;
    }

    // Generate unique job ID for this Excel export
    const jobId = `excel-${Date.now()}`;

    // Remove savedReportName from parameters before export
    const { savedReportName, ...paramsWithoutSavedName } = reportParams;

    const requestBody = {
      reportId: reportId.toString(),
      menuId: menuId,
      format: 'EXCELOPENXML',
      userID: parseInt(sessionData.userId, 10),
      adminUser: sessionData.isAdmin === true || sessionData.isAdmin === 'true',
      parameters: paramsWithoutSavedName,
      connectionId: signalRService.getConnectionId() || connectionId,
      jobId: jobId
    };
    
    // Build and store the export parameters summary for this job's tooltip
    let paramSummary;
    if (report && Array.isArray(report.parameters)) {
      paramSummary = report.parameters
        .filter(param => paramsWithoutSavedName.hasOwnProperty(param.paramName))
        .map(param => {
          const value = paramsWithoutSavedName[param.paramName];
          const paramName = param.paramName;
          let displayValue = value;
          if (param.validValues && Array.isArray(param.validValues)) {
            if (typeof value === 'string' && value.includes(',')) {
              displayValue = value.split(',').map(v => {
                const trimmedValue = v.trim();
                const option = param.validValues.find(opt => 
                  String(opt.value || opt.text || opt) === trimmedValue
                );
                return option ? (option.text || option.label || option.value || option) : trimmedValue;
              }).join(',');
            } else {
              const option = param.validValues.find(opt => 
                String(opt.value || opt.text || opt) === String(value)
              );
              displayValue = option ? (option.text || option.label || option.value || option) : value;
            }
          } else if (Array.isArray(value)) {
            displayValue = value.join(',');
          }
          return `${paramName}:${displayValue}`;
        })
        .join('\n');
    } else {
      paramSummary = Object.entries(paramsWithoutSavedName)
        .map(([k, v]) => `${k}:${Array.isArray(v) ? v.join(',') : v}`)
        .join('\n');
    }
    exportParamSummariesRef.current[jobId] = paramSummary;
    lastExportParamSummaryRef.current = paramSummary; // legacy fallback

      // Make fire-and-forget API call to ExportToExcel - SignalR handles everything
      axios.post(
        `${API_URL}/ViewReport/ExportToExcel`,
        requestBody
      ).then(response => {
        if (response.data && response.data.message === 'No Records to Export') {
          setError('No Records to Export');
          setExecuting(false);
          setLoading(false);
        } else {
          // HTTP fallback: ensure notification is success even if SignalR is unreliable
          const excelFileName = response?.data?.FileName || response?.data?.fileName;
          if (excelFileName) {
            savedReportFilesRef.current[jobId] = excelFileName;
            savedReportNameRef.current = excelFileName;
            setNotifications(prev => {
              const next = new Map(prev);
              const existing = prev.get(jobId);
              if (!existing || existing.status !== 'success') {
                next.set(jobId, {
                  message: `${excelFileName} exported successfully`,
                  status: 'success',
                  filePath: null
                });
              }
              return next;
            });
          }
          console.log('Excel export initiated, SignalR will handle everything...');
        }
      }).catch(err => {
        console.error('Failed to initiate Excel export:', err);
        setError(`Failed to initiate Excel export: ${err.message}`);
        setExecuting(false);
        setLoading(false);
      });
  };

  // Validate parameters before report execution
  const validateParameters = () => {
    if (!report?.parameters || report.parameters.length === 0) {
      return true; // No parameters to validate
    }
    
    // Check for missing required parameters
    for (const param of report.parameters) {
      // Get parameter type (normalize to lowercase for comparison)
      const paramType = (param.paramType || param.parameterType || param.type || 'Text').toLowerCase();
      
      // Skip validation for hidden parameters
      if (paramType === 'hidden') {
        continue;
      }
      
      if (param.required && 
          (!reportParams[param.paramName] || reportParams[param.paramName] === '')) {
        setError(`Required parameter "${param.label}" is missing.`);
        return false;
      }
    }
    
    return true;
  };

  // Check if a parameter should be disabled based on its dependencies
  const shouldDisableParameter = (param) => {
    if (!param.dependsOn ||
        (Array.isArray(param.dependsOn) && param.dependsOn.length === 0) ||
        (typeof param.dependsOn === 'string' && param.dependsOn === '')) {
      return false; // No dependencies, parameter is enabled
    }

    // Helper function to check if a dependency should be considered
    const shouldCheckDependency = (dependencyName) => {
      // Find the dependency parameter in the report
      const dependencyParam = report?.parameters?.find(p => p.paramName === dependencyName);

      if (dependencyParam) {
        const depParamType = (dependencyParam.paramType || dependencyParam.parameterType || dependencyParam.type || '').toLowerCase();

        // If dependency is hidden and has no value, ignore it (don't disable the control)
        if (depParamType === 'hidden') {
          const hasValue = reportParams[dependencyName] && reportParams[dependencyName] !== '';
          if (!hasValue) {
            console.log(`[Dependency] Ignoring hidden param "${dependencyName}" without value for "${param.paramName}"`);
            return false; // Don't check this dependency
          }
        }
      }

      // Check if the dependency value is missing
      return !reportParams[dependencyName] || reportParams[dependencyName] === '';
    };

    // Check if any dependency is missing
    if (Array.isArray(param.dependsOn)) {
      // For parameters with multiple dependencies, check all of them
      // Only disable if at least one non-hidden dependency is missing
      return param.dependsOn.some(dependencyName => shouldCheckDependency(dependencyName));
    } else if (typeof param.dependsOn === 'string') {
      // For parameters with a single dependency as string
      const dependencyName = param.dependsOn;
      return shouldCheckDependency(dependencyName);
    }

    return false;
  };

  // CheckboxGroup component for multi-select parameters
  const CheckboxGroup = ({ paramName, options, selectedValues, isDisabled }) => {
    // Convert selectedValues to array if it's a string with comma-separated values
    // and normalize all values to strings so comparisons are consistent.
    const selectedValuesArray = typeof selectedValues === 'string'
      ? selectedValues.split(',').filter(Boolean).map(String)
      : Array.isArray(selectedValues) ? selectedValues.map(String) : [];

    // Determine open state from parent-level `openParamDropdown` so the dropdown
    // doesn't close when interacting with its contents. Only one dropdown is
    // open at a time; opening another closes the previous.
    const isOpen = openParamDropdown === paramName;

    return (
      <div className="checkbox-group-container" data-param={paramName} style={{ position: 'relative' }}>
        {/* Dropdown header - shows selected count and acts as toggle */}
        <div
          className="selected-values-summary"
          onClick={() => {
            if (isDisabled) return;
            if (isOpen) setOpenParamDropdown(null);
            else setOpenParamDropdown(paramName);
          }}
          onKeyDown={(e) => {
            if (isDisabled) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (isOpen) setOpenParamDropdown(null);
              else setOpenParamDropdown(paramName);
            }
          }}
          tabIndex={isDisabled ? -1 : 0}
          role="button"
          aria-expanded={isOpen}
          aria-disabled={isDisabled}
          aria-label={`${selectedValuesArray.length === 0 ? "Select options" : `${selectedValuesArray.length} of ${options.length} selected`} for ${paramName}`}
          style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
        >
          <span className="dropdown-summary-text">
            {selectedValuesArray.length === 0
              ? "Select options..."
              : `${selectedValuesArray.length} of ${options.length} selected`}
          </span>
          <span style={{ marginLeft: 'auto' }}>▼</span>
        </div>

        {isOpen && (
          <div
            className="checkbox-options-dropdown"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 1200,
              width: '100%',
              background: '#fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              borderRadius: 4,
              marginTop: 6
            }}
          >
            <span
              className="close-dropdown-panel"
              onClick={() => setOpenParamDropdown(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpenParamDropdown(null);
                }
              }}
              tabIndex="0"
              role="button"
              aria-label="Close dropdown"
              style={{
                position: 'absolute',
                right: 8,
                top: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="9" stroke="#333" strokeWidth="1" fill="none" />
                <path d="M6 6l8 8M14 6L6 14" fill="none" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>

            {/* Always render select/unselect scaffold so dropdown maintains consistent appearance */}
            <div className="select-unselect-container">
              <div className="select-unselect-options">
                <div
                  className={`select-all-option ${options.length === 0 ? 'disabled' : ''}`}
                  onClick={() => {
                    if (options.length === 0) return;
                    const allValues = options.map(opt => String((opt && opt.value) != null ? opt.value : opt));
                    handleParameterChange(paramName, allValues, true);
                  }}
                  onKeyDown={(e) => {
                    if (options.length === 0) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      const allValues = options.map(opt => String((opt && opt.value) != null ? opt.value : opt));
                      handleParameterChange(paramName, allValues, true);
                    }
                  }}
                  tabIndex={options.length === 0 ? -1 : 0}
                  role="button"
                  aria-disabled={options.length === 0}
                  aria-label="Select all options"
                >
                  <span className="option-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 8l4 4 8-8" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className="option-text">Select all</span>
                </div>
                <div
                  className={`unselect-all-option ${options.length === 0 ? 'disabled' : ''}`}
                  onClick={() => {
                    if (options.length === 0) return;
                    handleParameterChange(paramName, [], true);
                  }}
                  onKeyDown={(e) => {
                    if (options.length === 0) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleParameterChange(paramName, [], true);
                    }
                  }}
                  tabIndex={options.length === 0 ? -1 : 0}
                  role="button"
                  aria-disabled={options.length === 0}
                  aria-label="Unselect all options"
                >
                  <span className="option-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 3l10 10M13 3L3 13" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className="option-text">Unselect all</span>
                </div>
              </div>
            </div>

            {/* Scrollable checkbox container */}
            <div className="checkbox-group-scrollable" style={{ maxHeight: 260, overflowY: 'auto', marginTop: 8 }}>
              {options.length === 0 ? (
                <div className="no-options-message">{getParameterSelectionMessage(paramName)}</div>
              ) : (
                options.map((option, index) => {
                  const optionValue = String((option && (option.value ?? option.Value)) != null ? (option.value ?? option.Value) : option);
                  const optionLabel = option.label || option.Label || option.text || option.value || option.Value || option;
                  const isChecked = selectedValuesArray.includes(optionValue);

                  return (
                    <div key={`${optionValue}-${index}`} className="checkbox-item">
                      <label>
                        <input
                          type="checkbox"
                          value={optionValue}
                          checked={isChecked}
                          onChange={(e) => {
                            let newSelectedValues;
                            if (e.target.checked) {
                              newSelectedValues = [...selectedValuesArray, optionValue];
                            } else {
                              newSelectedValues = selectedValuesArray.filter(v => v !== optionValue);
                            }
                            handleParameterChange(paramName, newSelectedValues, true);
                          }}
                          disabled={isDisabled}
                        />
                        <span className="checkbox-label">{optionLabel}</span>
                      </label>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer with clear button */}
            {selectedValuesArray.length > 0 && (
              <div className="checkbox-footer" style={{ marginTop: 8 }}>
                <button
                  className="clear-selection-btn"
                  onClick={() => handleParameterChange(paramName, [], true)}
                  disabled={isDisabled}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render select control for dropdown parameters
  const renderSelectControl = (paramName, options, selectedValues, isMultiValue, isDisabled, label) => {
    return (
      <select
        id={`param-${paramName}`}
        value={selectedValues}
        onChange={(e) => {
          // Get selected options for multiselect
          if (isMultiValue) {
            const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
            handleParameterChange(paramName, selectedOptions, true);
          } else {
            handleParameterChange(paramName, e.target.value);
          }
        }}
        className={`form-control ${isMultiValue ? 'multi-select' : ''}`}
        multiple={isMultiValue}
        disabled={isDisabled}
        size={isMultiValue && options.length > 0 ? Math.min(options.length, 5) : 1}
      >
        {!isMultiValue && <option value="">Select {label}</option>}
        {options.map((option, index) => {
          const optionValue = option.value || option;
          const optionLabel = option.label || option.text || option.value || option;
          
          return (
            <option 
              key={`${optionValue}-${index}`} 
              value={optionValue}
            >
              {optionLabel}
            </option>
          );
        })}
      </select>
    );
  };
  
  // Render appropriate control based on parameter type
  const renderParameterControl = (param) => {
    const paramName = param.paramName || param.name;
    const paramType = param.parameterType || param.paramType || 'Text';
    const isDisabled = shouldDisableParameter(param);
    
    // Enhanced check for multi-value parameters to include any parameter with paramType 'combobox'
    const isMultiValue = paramType.toLowerCase() === 'multivalue' || 
                         paramType.toLowerCase() === 'combobox' ||
                         (param.multiValue === true);
                         
    // Check if current value is a comma-separated string that needs to be split for multi-select
    let currentValue = reportParams[paramName] || '';
    let selectedValues = currentValue;
    
    if (isMultiValue && typeof currentValue === 'string' && currentValue.includes(',')) {
      selectedValues = currentValue.split(',');
    }
    
    // Always use checkbox UI for combobox or multivalue parameters
    if ((paramType.toLowerCase() === 'combobox' || paramType.toLowerCase() === 'multivalue' || param.multiValue === true)) {
      // Get options for the parameter
      const options = param.validValues || param.values || [];
      return <CheckboxGroup 
        paramName={paramName}
        options={options}
        selectedValues={selectedValues}
        isDisabled={isDisabled}
      />;
    }
    
    // For other parameter types
    switch (paramType.toLowerCase()) {
      case 'boolean':
        return (
          <input
            type="checkbox"
            id={`param-${paramName}`}
            checked={reportParams[paramName] === 'True' || reportParams[paramName] === true}
            onChange={(e) => handleParameterChange(paramName, e.target.checked ? 'True' : 'False')}
            className="form-control"
            disabled={isDisabled}
          />
        );
      
      case 'calendar':
      case 'datetime':
      case 'date':
        // Updated date control with custom date picker
        const displayDate = formatDateDisplay(reportParams[paramName]);
        
        return (
          <div className="date-input-wrapper">
            <input
              type="text"
              id={`param-${paramName}`}
              value={displayDate}
              onChange={(e) => {
                const val = e.target.value;
                // Basic validation for MM/DD/YYYY format
                if (val === '' || /^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
                  // Convert MM/DD/YYYY to YYYY-MM-DD for internal state
                  if (val !== '') {
                    const [month, day, year] = val.split('/');
                    const isoDate = `${year}-${month}-${day}`;
                    handleParameterChange(paramName, isoDate);
                  } else {
                    handleParameterChange(paramName, '');
                  }
                }
              }}
              placeholder="MM/DD/YYYY"
              className="form-control date-input"
              disabled={isDisabled}
              onFocus={() => !isDisabled && setActiveDatePicker(paramName)}
            />
            <input
              type="date"
              ref={el => dateFieldRefs.current[paramName] = el}
              value={reportParams[paramName] || ''}
              onChange={(e) => {
                const dateVal = e.target.value;
                handleParameterChange(paramName, dateVal);
              }}
              className="hidden-date-input"
              tabIndex="-1"
              disabled={isDisabled}
            />
            <span
              className="date-picker-icon" 
              onClick={() => !isDisabled && showDatePicker(paramName)}
              onKeyDown={(e) => {
                if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  showDatePicker(paramName);
                }
              }}
              tabIndex={isDisabled ? -1 : 0}
              role="button"
              aria-disabled={isDisabled}
              aria-label="Open date picker"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
              </svg>
            </span>
            {renderDatePicker(paramName)}
          </div>
        );
      
      case 'dropdown':
      case 'select':
      case 'cascadedropdown': // Add support for cascadedropdown type
        const options = param.validValues || param.values || [];
        
        // Use the standard select control for these types
        return renderSelectControl(paramName, options, selectedValues, false, isDisabled, param.label);
      
      default:
        return (
          <input
            type="text"
            id={`param-${paramName}`}
            value={reportParams[paramName] || ''}
            onChange={(e) => handleParameterChange(paramName, e.target.value)}
            className="form-control"
            placeholder={`Enter ${param.label}`}
            disabled={isDisabled}
          />
        );
    }
  };

  // Function to inject custom scripts into iframe for better report rendering
  const injectScriptIntoIframe = (iframe) => {
    try {
      if (!iframe || !iframe.contentWindow || !iframe.contentDocument) {
        return; // Exit if iframe or its contents aren't available
      }
      
      // Add necessary scripts to help with report rendering
      const script = iframe.contentDocument.createElement('script');
      script.type = 'text/javascript';
      script.text = `
        // Add polyfills for SSRS functionality
        if (typeof $get === 'undefined') {
          window.$get = function(id) {
            return document.getElementById(id);
          };
        }
        
        // Handle any iframe-specific initialization
        document.addEventListener('DOMContentLoaded', function() {
          // Make links open in parent window
          const links = document.getElementsByTagName('a');
          for (let i = 0; i < links.length; i++) {
            links[i].target = '_parent';
          }
        });
      `;
      
      // Append the script to the iframe document
      iframe.contentDocument.head.appendChild(script);
    } catch (err) {
      console.error('Error injecting script into iframe:', err);
    }
  };

  // Function to handle methodology document download
  const handleMethodologyDownload = async () => {
    if (!reportId) {
      setError("Report ID is missing");
      return;
    }
    
    try {
      setExecuting(true);
      
      // Use API_URL from environment instead of hardcoded URL
      const response = await axios({
        method: 'GET',
        url: `${API_URL}/ViewReport/DownloadMethodology`,
        params: { reportId },
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream'
        }
      });
      
      // Get the filename from the Content-Disposition header or use a default name
      let filename = `Report-${reportId}-Methodology.xlsx`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          // Ensure filename has .xlsx extension if not specified
          if (!filename.toLowerCase().endsWith('.xlsx')) {
            filename += '.xlsx';
          }
        }
      }
      
      // Create a blob from the response with the correct Excel content type
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading methodology document:", err);
      if (err.response) {
        console.error('Error response:', {
          status: err.response.status,
          statusText: err.response.statusText,
          url: err.config?.url
        });
      }
      setError(`Failed to download methodology: ${err.message}`);
    } finally {
      setExecuting(false);
    }
  };
  
  // Helper function to handle file downloads
  const handleFileDownload = (response, defaultFilename) => {
    // Get the filename from the Content-Disposition header or use the default name
    let filename = defaultFilename;
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
        // Ensure filename has .xlsx extension if not specified
        if (!filename.toLowerCase().endsWith('.xlsx')) {
          filename += '.xlsx';
        }
      }
    }
    
    // Create a blob from the response with the correct Excel content type
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Setup SignalR connection and handlers with connection ID state
  useEffect(() => {
    let connection = null;
    
    // Initialize SignalR connection
    const initializeSignalR = async () => {
      try {
        // Initialize SignalR connection to reportHub using the correct method name
        connection = await signalRService.initialize('/reportHub');
        
        // Store the connectionId for export requests
        if (connection && connection.connectionId) {
          setConnectionId(connection.connectionId);
        }
        
        // Register handler for receiving file path and status message from server via SignalR
        connection.on('ReceiveReportStatus', async (jobId, filePath, message) => {
          console.log('ReceiveReportStatus called with:', { jobId, filePath, message });

          // Determine status based on the message content with improved error detection
          let status = 'error'; // Default to error for safety

          if (message) {
            const messageStr = String(message).toLowerCase();
            
            // Check for errors FIRST (most important to catch)
            if (messageStr.includes('error') || 
                messageStr.includes('failed') || 
                messageStr.includes('fail') || 
                messageStr.includes('exception') || 
                messageStr.includes('timeout') ||
                messageStr.includes('could not find') ||
                messageStr.includes('access denied') ||
                messageStr.includes('unauthorized') ||
                messageStr.startsWith('error ')) {
              status = 'error';
            } else if (messageStr.includes('inprogress') || 
                       messageStr.includes('in progress') || 
                       messageStr.includes('processing')) {
              status = 'in-progress';
            } else if (messageStr.includes('successfully') && !messageStr.includes('error') || 
                       messageStr.includes('completed successfully') ||
                       (messageStr.includes('exported') && !messageStr.includes('error')) ||
                       messageStr.includes('export complete')) {
              status = 'success';
            }
          }

          console.log('Determined status:', status, 'for jobId:', jobId, 'message:', message);

          // Reset executing/loading state for final statuses (success or error)
          if (status === 'success' || status === 'error') {
            setExecuting(false);
            setLoading(false);
          }

          // Add/update notification entry in the Map keyed by jobId
          const notifKey = jobId || `unknown-${Date.now()}`;
          setNotifications(prev => {
            const next = new Map(prev);
            next.set(notifKey, {
              message: status === 'success' ? `${message}` : message || 'Unknown status received',
              status,
              filePath
            });
            return next;
          });

          // Store file info for click-to-download on successful export
          if (status === 'success' && filePath) {
            try {
              const fileName = filePath.split('\\').pop().split('/').pop();
              if (fileName) {
                savedReportFilesRef.current[notifKey] = fileName;
                savedReportNameRef.current = fileName; // legacy fallback
              }
            } catch (err) {
              console.error('Error extracting file name from path:', err);
            }
          }
        });
        
        return connection;
      } catch (error) {
        console.error('Failed to initialize SignalR connection:', error);
        return null;
      }
    };
    
    initializeSignalR();
    
    // Clean up on component unmount
    return () => {
      if (connection) {
        connection.off('ReceiveReportStatus');
        signalRService.closeConnection();
      }
    };
  }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to handle manual download when a notification bar is clicked
  const handleDownloadFromNotification = async (jobId, filePath) => {
    const fileName = (jobId && savedReportFilesRef.current[jobId])
      || savedReportNameRef.current // legacy fallback
      || filePath?.split('\\').pop().split('/').pop();

    if (!fileName) {
      console.error('No saved report name available for download');
      return;
    }

    try {
      const ext = fileName.split('.').pop().toLowerCase() || 'xlsx';
      const blob = await downloadReport(
        reportId, 
        ext, 
        { 
          savedReportName: fileName,
          adminUser: sessionData.isAdmin ? 'true' : 'false'
        }, 
        menuId, 
        sessionData.userId
      );

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading from notification:', error);
    }
  };
  
  // Initialize SignalR connection
  const initializeSignalRConnection = async () => {
    if (!connectionId) {
      try {
        // Initialize SignalR connection if not already established
        const connection = await signalRService.initialize('/reportHub');
        
        if (connection && connection.connectionId) {
          setConnectionId(connection.connectionId);
          
          // Show a test toast to confirm notifications are working
          //showStatusToast('Toast notification system initialized', 'success');
          
          return connection;
        } else {
          console.warn('Failed to get connection ID from SignalR');
          return null;
        }
      } catch (signalrError) {
        console.error('Error establishing SignalR connection:', signalrError);
        return null;
      }
    }
    return true; // Connection already exists
  };

  // Handle collapse/expand toggle for parameter section
  const handleCollapseToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Handle export dropdown toggle - only toggle visibility, don't make API calls
  const handleExportDropdownToggle = async () => {
    // Only establish connection when opening the dropdown
    if (!showExportDropdown) {
      await initializeSignalRConnection();
      
      // Additional check to ensure we have a valid auth token when opening the dropdown
      try {
        // Import auth service dynamically to avoid circular dependencies
        const authModule = await import('../services/authService');
        if (authModule && authModule.authService) {
          const token = await authModule.authService.acquireTokenForAPI();
          console.log('Export dropdown: Auth token available:', !!token);
          
          // Store token in session storage for immediate use
          if (token && token !== 'cookie-based-auth') {
            sessionStorage.setItem('authToken', token);
          }
        }
      } catch (authError) {
        console.warn('Error initializing auth for export:', authError);
      }
    }
    
    // Toggle dropdown visibility
    setShowExportDropdown(!showExportDropdown);
  };
  
  // Function to show or update toast based on report status
  const showStatusToast = (message, status) => {
    const toastContent = (
      <div className={`report-notification ${status}`}>
        {message}
        {status === 'success' && <span className="close-icon">×</span>}
      </div>
    );
    
    // If there's already a toast showing, update it instead of creating a new one
    if (reportStatusToastId.current) {
      toast.update(reportStatusToastId.current, {
        render: toastContent, 
        type: status === 'success' ? 'success' : 
              status === 'error' ? 'error' : 'info',
        className: `report-toast ${status}`,
        autoClose: status === 'in-progress' ? false : 15000,
        isLoading: status === 'in-progress'
      });
    } else {
      // Create new toast if one doesn't exist yet
      if (status === 'success') {
        reportStatusToastId.current = toast.success(toastContent, {
          position: "bottom-right",
          autoClose: 15000,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          className: `report-toast ${status}`,
          onClick: status === 'success' ? handleDownloadFromToast : undefined
        });
      } else if (status === 'error') {
        reportStatusToastId.current = toast.error(toastContent, {
          position: "bottom-right",
          autoClose: 15000,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          className: `report-toast ${status}`
        });
      } else {
        // For in-progress
        reportStatusToastId.current = toast.info(toastContent, {
          position: "bottom-right",
          autoClose: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: false,
          className: `report-toast ${status}`,
          isLoading: true
        });
      }
    }
    
    return reportStatusToastId.current;
  };
  
  // Function to handle download when toast is clicked
  const handleDownloadFromToast = () => {
    // If download has already started, don't call API again
    if (downloadStartedRef.current) {
      // Still dismiss the toast if clicked again
      if (reportStatusToastId.current) {
        toast.dismiss(reportStatusToastId.current);
        reportStatusToastId.current = null;
      }
      
      return;
    }
    
    // Set flag to indicate download has started
    downloadStartedRef.current = true;
    
    // Dismiss the toast immediately when clicked for download
    if (reportStatusToastId.current) {
      toast.dismiss(reportStatusToastId.current);
      reportStatusToastId.current = null;
    }
    
    // Check for savedReportName in ref first (more reliable), then fall back to reportParams
    let reportName = savedReportNameRef.current || reportParams.savedReportName;
    
    if (reportName) {
      try {
        // Clean up the reportName to remove the "&rptid=X" suffix if it exists
        if (reportName.includes('&rptid=')) {
          reportName = reportName.split('&rptid=')[0];
        }
        
        // Strip out any URL encoding completely to get the raw filename
        let rawReportName = reportName;
        
        // First, try to decode if it looks encoded
        if (rawReportName.includes('%')) {
          try {
            rawReportName = decodeURIComponent(rawReportName);
          } catch (e) {
            console.error('Error decoding URI component:', e);
          }
        }
        
        // Replace any remaining + with spaces
        rawReportName = rawReportName.replace(/\+/g, ' ');
      
        const downloadUrl = `${API_URL}/DownloadReport/download`;
        
        axios.get(
          `${downloadUrl}?reportname=${encodeURIComponent(rawReportName)}&rptid=${reportId}`, 
          { 
            responseType: 'blob' 
          }
        )
        .then(response => {
          // Create a blob from the response
          const blob = new Blob([response.data], { 
            type: response.headers['content-type'] 
          });
          
          // Extract file name - prioritize backend savedReportName for Excel exports\n          let fileName;\n          const contentDisposition = response.headers['content-disposition'];\n          if (contentDisposition) {\n            const fileNameMatch = contentDisposition.match(/filename[^;=\\n]*=((['\"]).*?\\2|[^;\\n]*)/); \n            if (fileNameMatch && fileNameMatch[1]) {\n              fileName = fileNameMatch[1].replace(/['\"]/g, '');\n            }\n          }\n          \n          // If no filename from headers, use the backend savedReportName as primary source\n          if (!fileName && rawReportName) {\n            // Use the raw report name from backend (already cleaned)\n            fileName = rawReportName;\n            \n            // Ensure it has the correct extension for Excel exports\n            if (!fileName.toLowerCase().endsWith('.xlsx') && \n                !fileName.toLowerCase().endsWith('.xls') &&\n                !fileName.toLowerCase().endsWith('.csv')) {\n              fileName += '.xlsx';\n            }\n          }\n          \n          // Final fallback: construct from report ID\n          if (!fileName) {\n            fileName = `Report-${reportId}.xlsx`;\n          }
                    let fileName;
          const contentDisposition = response.headers['content-disposition'];
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (fileNameMatch && fileNameMatch[1]) {
              fileName = fileNameMatch[1].replace(/['"]/g, '');
            }
          }
          
          // If no filename from headers, construct from raw report name
          if (!fileName) {
            // Extract file extension from the raw report name if possible
            const extensionMatch = rawReportName.match(/\.(xlsx|pdf|docx|xml|csv)$/i);
            const extension = extensionMatch ? extensionMatch[0] : '.xlsx';
            fileName = `Report-${reportId}${extension}`;
          }
          
          // Create download link and trigger download
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          
          // Clean up
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          
          // Show success notification
          toast.success(`Report downloaded successfully as ${fileName}`, {
            position: "bottom-right",
            autoClose: 3000
          });
          
          // Close SignalR connection after successful download
          signalRService.closeConnection();
          setConnectionId(null);
        })
        .catch(error => {
          console.error('Error downloading report:', error);
          toast.error('Failed to download report. Please try again.');
        })
        .finally(() => {
          // Reset the download flag after complete (success or failure) 
          // so user can try again if needed
          setTimeout(() => {
            downloadStartedRef.current = false;
          }, 3000); // Add a small delay before allowing another download
        });
      } catch (error) {
        console.error('Error processing download:', error);
        toast.error('Error processing download. Please try again.');
        // Reset the download flag on error
        downloadStartedRef.current = false;
      }
    } else {
      console.error('No report name available for download');
      toast.error('Cannot download: no report name available');
      // Reset the download flag on error
      downloadStartedRef.current = false;
    }
  };
  
  if (loading) {
    return (
      <div className="loader-container" style={{ textAlign: 'center', padding: '50px' }}>
        <img src="/ajax-loading.gif" alt="Loading..." style={{ width: '64px', height: '64px' }} />
      </div>
    );
  }

  // Derived boolean: any dependent dropdown is loading
  const isParamLoading = Object.values(dropdownLoading || {}).some(v => v === true);

   return (
    <>
    <div className="view-report-container" style={{ position: 'relative' }}>
      {(executing || isParamLoading) && (
        <div
          className="view-loading-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200
          }}
        >
          <img src="/ajax-loading.gif" alt="Loading..." style={{ width: '64px', height: '64px' }} />
        </div>
      )}
      {/* Report header - Updated to center alignment */}
      <div className="report-header" style={{ textAlign: 'center' }}>
        <h1>{report?.reportName }</h1>
        <button
          className="collapse-btn"
          onClick={handleCollapseToggle}
          aria-label={isCollapsed ? "Expand" : "Collapse"}
        >
          <span className={isCollapsed ? "arrow-down" : "arrow-up"}>
            {isCollapsed ? "▼" : "▲"}
          </span>
        </button>
      </div>

      {/* Report description - Updated to center alignment */}
      {report?.reportDescription && (
        <div className="report-description" style={{ textAlign: 'center' }}>
          <p>{report.reportDescription}</p>
        </div>
      )}

      {/* Parameter section - exact layout from the sample image */}
      <div className={`parameter-section ${isCollapsed ? 'collapsed' : ''}`}>
        {!isCollapsed && (
          <>
           
            {report && report.parameters && report.parameters.length > 0 ? (
          <div className="parameter-form">
            {/* Parameter fields container - horizontal layout */}
            <div className="param-fields-container">
              {report.parameters.filter((param) => {
                const paramType = (param.paramType || param.parameterType || param.type || '').toLowerCase();
                return paramType !== 'hidden';
              }).map((param) => (
                <div key={param.paramId || param.paramName} className="param-field-column">
                  <div className="param-label">
                    {param.label}
                    {param.required && <span className="required">*</span>}
                  </div>
                  <div className="param-control">
                    {renderParameterControl(param)}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Action buttons row */}
            <div className="action-buttons-row">
              {showExportToExcelButton && (
                <button 
                  className="btn-export-excel"
                  onClick={handleExportToExcel}
                  disabled={executing}
                >
                  Export to Excel
                </button>
              )}
              
              {showViewReportButton && (
                <button 
                  className="btn-view-report"
                  onClick={handleViewReport}
                  disabled={executing}
                >
                  View Report
                </button>
              )}
              
              {showMethodologyButton && (
                <button 
                  className="btn-methodology"
                  onClick={handleMethodologyDownload}
                  disabled={executing}
                >
                  Methodology
                </button>
              )}
              
              {showExportButton && (
                <div className="export-dropdown-container">
                  <button 
                    className="btn-export dropdown-toggle"
                    onClick={handleExportDropdownToggle}
                    disabled={executing}
                    ref={exportButtonRef}
                  >
                    Export
                  </button>
                    
                  {showExportDropdown && (
                    <div className="export-dropdown-menu">
                      <div>Options</div>
                      <div style={{display: 'flex', flexWrap: 'wrap'}}>
                        <button 
                          className="export-option"
                          onClick={() => handleExportReport('EXCEL')}
                          disabled={executing}
                        >
                          <img src="/images/Excel.png" alt="Excel" />
                          Excel
                        </button>
                        <button 
                          className="export-option"
                          onClick={() => handleExportReport('pdf')}
                          disabled={executing}
                        >
                          <img src="/images/pdf.png" alt="PDF" />
                          PDF
                        </button>
                        <button 
                          className="export-option"
                          onClick={() => handleExportReport('docx')}
                          disabled={executing}
                        >
                          <img src="/images/word.png" alt="Word" />
                          Word
                        </button>
                        <button 
                          className="export-option"
                          onClick={() => handleExportReport('csv')}
                          disabled={executing}
                        >
                          <img src="/images/Csv.png" alt="CSV" />
                          CSV
                        </button>
                        <button 
                          className="export-option"
                          onClick={() => handleExportReport('xml')}
                          disabled={executing}
                        >
                          <img src="/images/Xml.png" alt="XML" />
                          XML
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          // Check if there are any visible (non-hidden) parameters
          report && report.parameters && report.parameters.filter(param => {
            const paramType = param.parameterType || param.paramType || '';
            return paramType.toLowerCase() !== 'hidden';
          }).length === 0 ? (
          <div className="no-parameters-message">
            <p>This report has no parameters. You can view or export it directly.</p>
            <div className="action-buttons-row">
              {showExportToExcelButton === true && (
                <button 
                  className="btn-export-excel"
                  onClick={handleExportToExcel}
                  disabled={executing}
                >
                  Export to Excel
                </button>
              )}
              
              {showViewReportButton === true && (
                <button 
                  className="btn-view-report"
                  onClick={handleViewReport}
                  disabled={executing}
                >
                  View Report
                </button>
              )}
              
              {/* Export dropdown button */}
              {showExportButton === true && (
              <div className="export-dropdown-container">
                <button 
                  className="btn-export dropdown-toggle"
                  onClick={handleExportDropdownToggle}
                  disabled={executing}
                  ref={exportButtonRef}
                >
                  Export
                </button>
                  
                {showExportDropdown && (
                  <div className="export-dropdown-menu">
                    <div>Options</div>
                    <div style={{display: 'flex', flexWrap: 'wrap'}}>
                      <button 
                        className="export-option"
                        onClick={() => handleExportReport('EXCEL')}
                        disabled={executing}
                      >
                        <img src="/images/Excel.png" alt="Excel" />
                        Excel
                      </button>
                      <button 
                        className="export-option"
                        onClick={() => handleExportReport('pdf')}
                        disabled={executing}
                      >
                        <img src="/images/pdf.png" alt="PDF" />
                        PDF
                      </button>
                      <button 
                        className="export-option"
                        onClick={() => handleExportReport('docx')}
                        disabled={executing}
                      >
                        <img src="/images/word.png" alt="Word" />
                        Word
                      </button>
                      <button 
                        className="export-option"
                        onClick={() => handleExportReport('csv')}
                        disabled={executing}
                      >
                        <img src="/images/Csv.png" alt="CSV" />
                        CSV
                      </button>
                      <button 
                        className="export-option"
                        onClick={() => handleExportReport('xml')}
                        disabled={executing}
                      >
                        <img src="/images/Xml.png" alt="XML" />
                        XML
                      </button>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
          ) : null
        )}

        {error && <div style={{ color: 'red', fontWeight: 'bold' }}>{error}</div>}
          </>
        )}
      </div>
      
      {/* Report viewer - now using React state to manage content */}
      {showReport && (
        <div className="report-viewer">
          {executing ? (
            <LoadingIndicator message="Generating report..." />
          ) : pdfContent ? (
            <div className="pdf-viewer-container">
              <PDFViewer base64Data={pdfContent} />
            </div>
          ) : htmlContent && (
            <div 
              id="report-container"
              className="report-html-container"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          )}
        </div>
      )}
    </div>
    
    {/* Multi-notification stack — one scrollable container, one bar per export job */}
    {notifications.size > 0 && createPortal(
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9998,
          display: 'flex',
          flexDirection: 'column-reverse',  /* newest at bottom, older scroll upward */
          gap: '10px',
          maxHeight: 'calc(100vh - 40px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingTop: '8px',
          scrollbarWidth: 'none',
        }}
      >
        {[...notifications.entries()].map(([jobId, n]) => {
          const formatLabel = jobId.split('-')[0].toUpperCase();
          const isHovered = hoveredJobId === jobId;
          return (
            <div key={jobId} style={{ position: 'relative' }}>
              {/* Param tooltip — only shown when this bar is hovered */}
              {isHovered && exportParamSummariesRef.current[jobId] && (
                <div
                  className="custom-tooltip"
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    right: 0,
                    marginBottom: '6px',
                    minWidth: '200px',
                    maxWidth: '600px',
                    background: '#fff9c4',
                    color: '#444',
                    border: '2px solid #f0ad4e',
                    borderRadius: '8px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                    padding: '12px 18px',
                    fontSize: '12px',
                    zIndex: 10000,
                    textAlign: 'left',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-line',
                    pointerEvents: 'none',
                  }}
                >
                  {exportParamSummariesRef.current[jobId]}
                </div>
              )}
              <div
                className="custom-notification"
                style={{
                  backgroundColor:
                    n.status === 'in-progress' ? '#f0ad4e' :
                    n.status === 'success' ? '#5cb85c' :
                    n.status === 'error' ? '#c9302c' : '#f0ad4e',
                  borderLeftColor:
                    n.status === 'in-progress' ? '#ec971f' :
                    n.status === 'success' ? '#449d44' :
                    n.status === 'error' ? '#c9302c' : '#ec971f',
                  width: '505px',
                  cursor: n.status === 'success' ? 'pointer' : 'default',
                  borderRadius: '6px',
                  borderLeftWidth: '4px',
                  borderLeftStyle: 'solid',
                  boxShadow: isHovered ? '0 6px 20px rgba(0,0,0,0.25)' : '0 4px 12px rgba(0,0,0,0.15)',
                  transform: isHovered ? 'translateY(-2px)' : 'translateY(0px)',
                  padding: '10px 40px 10px 12px',
                  color: 'black',
                  fontSize: '12px',
                  lineHeight: '17px',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  position: 'relative',
                  flexShrink: 0,
                }}
                onClick={n.status === 'success' ? () => handleDownloadFromNotification(jobId, n.filePath) : undefined}
                onMouseEnter={() => setHoveredJobId(jobId)}
                onMouseLeave={() => setHoveredJobId(null)}
              >
                {/* Format badge — shows "EXCEL", "PDF", "CSV" etc */}
                <span style={{
                  display: 'inline-block',
                  background: 'rgba(0,0,0,0.18)',
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 700,
                  borderRadius: '3px',
                  padding: '1px 5px',
                  marginRight: '8px',
                  verticalAlign: 'middle',
                  letterSpacing: '0.5px',
                }}>
                  {formatLabel}
                </span>
                <span className="notification-content" style={{ verticalAlign: 'middle' }}>
                  {n.message}
                </span>
                {n.status === 'in-progress' && (
                  <div style={{ position: 'absolute', right: '45px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                    <div style={{
                      width: '18px', height: '18px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderRadius: '50%',
                      borderTop: '2px solid #fff',
                      animation: 'spin 1s linear infinite',
                    }} />
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setNotifications(prev => { const next = new Map(prev); next.delete(jobId); return next; });
                  }}
                  style={{
                    position: 'absolute', right: '8px', top: '8px',
                    background: 'none', border: 'none', color: '#fff',
                    fontSize: '18px', cursor: 'pointer'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          );
        })}
      </div>,
      document.body
    )}
    </>
  );
};

export default ViewReport2014Page;